import React, { FC } from "react";

import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, FormControlLabel, Typography } from "@mui/material";

import { ToSignCompleted } from "@/assets/images";
import { DateInput, TextInput } from "@/components/atoms";
import { documentApi } from "@/shared/apis";
import { SignDocumentRequest } from "@/shared/domain";

import { formSchemaCustomerCardSign } from "./formSchema";
import { customerCardSignDefaultValues } from "./formType";
import { StyledCustomerPreviewModal } from "./styled";

type CustomerCardSignModalProps = {
    isSigned: boolean;
    openModal: boolean;
    onRequestSign: () => any;
    onClose: () => void;
    requestId: string;
    signedIn: string;
    signedBy: string;
};

const CustomerCardSignModal: FC<CustomerCardSignModalProps> = ({
    isSigned,
    onClose,
    openModal,
    onRequestSign,
    requestId,
    signedIn,
    signedBy,
}) => {
    const { t } = useTranslation();

    const { id } = useParams();
    const policyholderId = Number(id);

    const { control, handleSubmit, watch } = useForm<SignDocumentRequest>({
        mode: "onBlur",
        defaultValues: customerCardSignDefaultValues(policyholderId, requestId, signedBy, signedIn),
        resolver: yupResolver(formSchemaCustomerCardSign),
    });

    const { mutate: signDocumentFromRequest, isLoading } = useMutation(documentApi.signDocumentFromRequest, {
        onSuccess: () => {
            onRequestSign();
        },
    });

    const handleSignDocument = (formValues: SignDocumentRequest) => {
        signDocumentFromRequest(formValues);
    };

    const approved = watch("approved");
    const signIn = watch("signedIn");

    return (
        <StyledCustomerPreviewModal
            classes={{ paper: "dialog-paper" }}
            className="customerPreviewModal"
            open={openModal}
            onClose={onClose}
        >
            <Typography color="var(--color-primary)" fontWeight="bold" textAlign="center">
                {t("ToSign.Confirm.Title")}
            </Typography>
            {!isSigned ? (
                <>
                    <div data-testid="modal-approved-form">
                        <TextInput
                            name="signedBy"
                            autoComplete="off"
                            control={control}
                            disabled
                            label={t("ToSign.Confirm.Name")}
                        />
                        <DateInput name="signedOn" label={t("ToSign.Confirm.Date")} control={control} disabled />
                        <TextInput
                            name="signedIn"
                            autoComplete="off"
                            control={control}
                            label={t("ToSign.Confirm.Place")}
                            required
                        />
                        <Controller
                            control={control}
                            name="approved"
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            inputProps={{
                                                // @ts-ignore
                                                "data-testid": "checkbox-approved",
                                            }}
                                            name="checkbox"
                                            color="primary"
                                            checked={value}
                                            onChange={(e) => onChange(e.target.checked)}
                                            onBlur={onBlur}
                                        />
                                    }
                                    label={t("ToSign.Confirm.Approved")}
                                    labelPlacement="end"
                                />
                            )}
                        />
                    </div>
                    <Button
                        disabled={!approved || !signIn || isLoading}
                        fullWidth
                        data-testid="to_sign_approved-button-approved"
                        onClick={handleSubmit(handleSignDocument)}
                    >
                        {t("ToSign.Confirm.Sign")}
                    </Button>
                </>
            ) : (
                <>
                    <div className="modalBody">
                        <ToSignCompleted />
                        <Typography
                            color="var(--color-grey-800)"
                            fontSize="var(--size-14)"
                            fontWeight="bold"
                            textAlign="center"
                        >
                            {t("ToSign.Confirm.Text")}
                        </Typography>
                    </div>
                    <Button fullWidth data-testid="to_sign_approved-button-approved" onClick={onClose}>
                        {t("Common.Ok")}
                    </Button>
                </>
            )}
        </StyledCustomerPreviewModal>
    );
};

export default CustomerCardSignModal;
